import type { TFunction } from 'i18next';
import { ArrowRight, Check, FileText } from 'lucide-react';

import { Badge } from '@/components/ui/badge';

import type {
  DispatchDangerousGoodDto,
  DispatchInProgressTableData,
  DispatchOrganizationDto,
  DispatchQuantityDto,
  DispatchRouteDto,
  DispatchSubprojectDto,
  DispatchTemperatureRangeDto
} from '@/entities/dispatch';
import { formatDisplayDate } from '@/shared/lib';
import type { ColumnPreset, DataTableColumn } from '@/shared/ui/data-table';
import { TemperatureRangeBadge } from '@/shared/ui/temperature-range-badge';

const EMPTY_CELL = '-';

/**
 * Origin → destination legs with the consignments and waybills travelling on
 * them. JSF renders this cell as an HTML blob (`BeanBase.getJourneyDestinations`);
 * here the same information arrives structured.
 */
const renderRoutes = (routes: DispatchRouteDto[] | undefined) => {
  if (!routes?.length) return EMPTY_CELL;

  return (
    <div className="flex flex-col gap-1.5">
      {routes.map((route, index) => (
        <div key={`${route.origin}-${route.destination}-${index}`} className="flex flex-col gap-1">
          <span className="flex items-center gap-1">
            {route.origin}
            <ArrowRight className="h-3 w-3 shrink-0" aria-hidden />
            {route.destination}
          </span>

          <div className="flex flex-wrap gap-1">
            {route.consignments.map((consignment) => (
              <Badge
                key={consignment.consignmentId}
                variant="outline"
                className="font-mono"
                title={consignment.senderOrganizationName}>
                {consignment.consignmentNumber}
                {consignment.senderOrganizationAbbreviation ? ` - ${consignment.senderOrganizationAbbreviation}` : ''}
              </Badge>
            ))}

            {route.consignments.flatMap((consignment) =>
              consignment.waybillNumbers.map((waybill) => (
                <Badge key={`${consignment.consignmentId}-${waybill}`} variant="outline" className="gap-1 font-mono">
                  <FileText className="h-3 w-3" aria-hidden />
                  {waybill}
                </Badge>
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

/** Per-line quantities as stacked `lineReference: qty unit` rows. */
const renderQuantities = (quantities: DispatchQuantityDto[] | undefined) => {
  if (!quantities?.length) return EMPTY_CELL;

  return (
    <div className="flex flex-col gap-0.5">
      {quantities.map((quantity, index) => (
        <span key={`${quantity.lineReference}-${index}`} title={quantity.productDescription}>
          {quantity.lineReference}: {quantity.quantity} {quantity.unit}
        </span>
      ))}
    </div>
  );
};

const renderTemperatureRanges = (ranges: DispatchTemperatureRangeDto[] | undefined) => {
  if (!ranges?.length) return EMPTY_CELL;

  return (
    <div className="flex flex-col gap-0.5">
      {ranges.map((range, index) => (
        <TemperatureRangeBadge
          key={`${range.minTemp}-${range.maxTemp}-${index}`}
          min={range.minTemp}
          max={range.maxTemp}
        />
      ))}
    </div>
  );
};

const renderOrganizations = (organizations: DispatchOrganizationDto[] | undefined) => {
  if (!organizations?.length) return EMPTY_CELL;

  return (
    <div className="flex flex-wrap gap-1">
      {organizations.map((organization, index) => (
        <Badge key={`${organization.abbreviation}-${index}`} variant="outline" title={organization.name}>
          {organization.abbreviation || organization.name}
        </Badge>
      ))}
    </div>
  );
};

const renderDangerousGoods = (goods: DispatchDangerousGoodDto[] | undefined) => {
  if (!goods?.length) return EMPTY_CELL;

  return (
    <div className="flex flex-col gap-0.5">
      {goods.map((good, index) => (
        <span key={`${good.unId}-${index}`} title={good.classDescription}>
          {good.unId}
          {good.description ? ` — ${good.description}` : ''}
        </span>
      ))}
    </div>
  );
};

const renderSubprojects = (subprojects: DispatchSubprojectDto[] | undefined) => {
  if (!subprojects?.length) return EMPTY_CELL;

  return subprojects.map((subproject) => subproject.code || subproject.description || EMPTY_CELL).join(', ');
};

const renderList = (values: string[] | undefined) => (values?.length ? values.join(', ') : EMPTY_CELL);

/**
 * Legacy `DecimalFormat` parity (`BeanBase`): `#,###,##0.0` for weight,
 * `#,###,##0.00` for volume, `#,###,##0` for value — all with the English
 * grouping separator, which is what the JSF screen prints.
 */
const formatNumber = (value: number | null | undefined, fractionDigits: number) =>
  typeof value === 'number'
    ? value.toLocaleString('en-US', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })
    : EMPTY_CELL;

/**
 * Every sortable column needs an `accessorKey`: TanStack's `getCanSort()` ends
 * with `!!column.accessorFn`, so a display column silently ignores
 * `enableSorting: true`. Sorting itself is server-side, the accessor is never
 * read — `cell` still owns the rendering.
 *
 * In-progress dispatch columns — JSF parity with
 * `fragments/dispatchtablefields.xhtml`: the default-visible set and order
 * match the legacy screen, everything the legacy marks `data-is-hidden` is
 * hidden here too and surfaced via Customize Columns. Sortable columns are the
 * ones the legacy table allows to sort (`dispatch.js` `noSortCols()` lists the
 * rest as aggregates over consignment lines).
 */
export const getDispatchInProgressColumns = (t: TFunction): DataTableColumn<DispatchInProgressTableData>[] => [
  {
    id: 'actualDispatchTime',
    accessorKey: 'actualDispatchTime',
    header: t('journey.actualDispatchTime.label', 'Actual Date of Dispatch'),
    width: 'w-[120px]',
    cell: (row) => <div className="text-xs text-muted-foreground">{formatDisplayDate(row.actualDispatchTime)}</div>,
    enableSorting: true,
    sortFieldId: 'actualDispatchTime'
  },
  {
    id: 'routes',
    accessorKey: 'routes',
    header: t('dispatch.journey.destination.label', 'Origin, Destination, Consignment, Waybill'),
    width: 'w-[340px]',
    cell: (row) => renderRoutes(row.routes),
    enableSorting: true,
    sortFieldId: 'destination'
  },
  {
    id: 'vehicleType',
    accessorKey: 'vehicleType',
    header: t('journey.vehicleType.label', 'Vehicle Type'),
    width: 'w-[120px]',
    cell: (row) => row.vehicleType || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'vehicleType'
  },
  {
    id: 'convoyName',
    accessorKey: 'convoyName',
    header: t('convoy.name.desc', 'Convoy'),
    width: 'w-[120px]',
    cell: (row) => row.convoyName || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'convoyName'
  },
  {
    id: 'transportCompany',
    accessorKey: 'transportCompany',
    header: t('journey.transportCompany.label', 'Transport Company'),
    width: 'w-[150px]',
    cell: (row) => row.transportCompany || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'transportCompany'
  },
  {
    id: 'remarks',
    accessorKey: 'remarks',
    header: t('journey.remarksDispatch.label', 'Remarks'),
    width: 'w-[220px]',
    cell: (row) => row.remarks || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'remarks'
  },
  {
    id: 'totalWeight',
    accessorKey: 'totalWeight',
    header: `${t('newcmr.line.totweight.label', 'Total weight')} (kg)`,
    width: 'w-[100px]',
    className: 'text-right',
    cell: (row) => formatNumber(row.totalWeight, 1),
    enableSorting: false
  },
  {
    id: 'totalVolume',
    accessorKey: 'totalVolume',
    header: `${t('newcmr.line.totvolume.label', 'Total volume')} (m³)`,
    width: 'w-[100px]',
    className: 'text-right',
    cell: (row) => formatNumber(row.totalVolume, 2),
    enableSorting: false
  },
  {
    id: 'numberOfPallets',
    accessorKey: 'numberOfPallets',
    header: t('dispatch.dispatch.popup.numberOfPallets.label', 'Number of pallets'),
    width: 'w-[110px]',
    className: 'text-right',
    cell: (row) => (typeof row.numberOfPallets === 'number' ? row.numberOfPallets : EMPTY_CELL),
    enableSorting: false,
    visible: false
  },

  /* --- detail columns (hidden by default, JSF parity) -------------------- */
  {
    id: 'totalValue',
    accessorKey: 'totalValue',
    header: t('tracking.servicereq.totval.label', 'Total Value (USD)'),
    width: 'w-[120px]',
    className: 'text-right',
    cell: (row) => formatNumber(row.totalValue, 0),
    enableSorting: false,
    visible: false
  },
  {
    id: 'expiryDates',
    header: t('requestline.expiryDate.label', 'Expiry Date'),
    width: 'w-[120px]',
    cell: (row) =>
      row.expiryDates?.length ? (
        <div className="text-xs text-muted-foreground">
          {row.expiryDates.map((date) => formatDisplayDate(date)).join(', ')}
        </div>
      ) : (
        EMPTY_CELL
      ),
    enableSorting: false,
    visible: false
  },
  {
    id: 'temperatureRanges',
    header: t('tracking.servicereq.temprange.label', 'Temperature Range'),
    width: 'w-[140px]',
    cell: (row) => renderTemperatureRanges(row.temperatureRanges),
    enableSorting: false,
    visible: false
  },
  {
    id: 'batchNumbers',
    header: t('requestline.batchNumber.label', 'Batch Number'),
    width: 'w-[130px]',
    cell: (row) => renderList(row.batchNumbers),
    enableSorting: false,
    visible: false
  },
  {
    id: 'senderOrganizations',
    header: t('newcmr.senderorg.label', 'Sender Organization'),
    width: 'w-[200px]',
    cell: (row) => renderOrganizations(row.senderOrganizations),
    enableSorting: false,
    visible: false
  },
  {
    id: 'receiverOrganizations',
    header: t('newcmr.receiverorg.label', 'Receiver Organization'),
    width: 'w-[200px]',
    cell: (row) => renderOrganizations(row.receiverOrganizations),
    enableSorting: false,
    visible: false
  },
  {
    id: 'dangerousGoods',
    header: t('requestline.dangerousGoodsUnid.label', 'Dangerous Goods UN Identification number'),
    width: 'w-[180px]',
    cell: (row) => renderDangerousGoods(row.dangerousGoods),
    enableSorting: false,
    visible: false
  },
  {
    id: 'ownersReferences',
    header: t('request.ownersRef.label', "Owner's Reference"),
    width: 'w-[130px]',
    cell: (row) => renderList(row.ownersReferences),
    enableSorting: false,
    visible: false
  },
  {
    id: 'dispatchPriority',
    accessorKey: 'dispatchPriority',
    header: t('dispatchPriority.description.desc', 'Dispatch Priority'),
    width: 'w-[130px]',
    cell: (row) =>
      row.dispatchPriority ? (
        <Badge variant="outline" title={row.dispatchPriority.description}>
          {row.dispatchPriority.name}
        </Badge>
      ) : (
        EMPTY_CELL
      ),
    enableSorting: true,
    sortFieldId: 'dispatchPriority',
    visible: false
  },
  {
    id: 'vehicleIdnf',
    accessorKey: 'vehicleIdnf',
    header: t('journey.vehicleIdnf.label', 'Vehicle Id'),
    width: 'w-[120px]',
    cell: (row) => row.vehicleIdnf || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'vehicleIdnf',
    visible: false
  },
  {
    id: 'driverName',
    accessorKey: 'driverName',
    header: t('journey.driverName.short', 'Driver Name'),
    width: 'w-[140px]',
    cell: (row) => row.driverName || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'driverName',
    visible: false
  },
  {
    id: 'vehicleCategory',
    accessorKey: 'vehicleCategory',
    header: t('journey.vehicleCategory.label', 'Vehicle Category'),
    width: 'w-[130px]',
    cell: (row) => row.vehicleCategory?.description || EMPTY_CELL,
    enableSorting: true,
    sortFieldId: 'vehicleCategory',
    visible: false
  },
  {
    id: 'productCategories',
    header: t('product.description.label', 'Product Category'),
    width: 'w-[200px]',
    cell: (row) => renderList(row.productCategories),
    enableSorting: false,
    visible: false
  },
  {
    id: 'ownerItemDescriptions',
    header: t('requestline.ownerItemDesc.desc', 'Owner Item Description'),
    width: 'w-[220px]',
    cell: (row) => renderList(row.ownerItemDescriptions),
    enableSorting: false,
    visible: false
  },
  {
    id: 'inventoryQuantities',
    header: t('tracking.servicereq.unitqty.label', 'Inventory Unit Quantity'),
    width: 'w-[180px]',
    cell: (row) => renderQuantities(row.inventoryQuantities),
    enableSorting: false,
    visible: false
  },
  {
    id: 'bundleQuantities',
    header: t('tracking.servicereq.handlingqty.label', 'Handling Unit Quantity'),
    width: 'w-[180px]',
    cell: (row) => renderQuantities(row.bundleQuantities),
    enableSorting: false,
    visible: false
  },
  {
    id: 'reportingQuantities',
    header: t('tracking.servicereq.reportingqty.label', 'Reporting Unit Quantity'),
    width: 'w-[180px]',
    cell: (row) => renderQuantities(row.reportingQuantities),
    enableSorting: false,
    visible: false
  },
  {
    id: 'subprojects',
    header: t('subproject.idnf.label', 'Sub-project code'),
    width: 'w-[130px]',
    cell: (row) => renderSubprojects(row.subprojects),
    enableSorting: false,
    visible: false
  },
  {
    id: 'journeyTemperatureRanges',
    header: t('journey.temperatureLoggers.label', 'Temperature Loggers'),
    width: 'w-[150px]',
    cell: (row) => renderTemperatureRanges(row.journeyTemperatureRanges),
    enableSorting: false,
    visible: false
  },
  {
    id: 'temperatureAnomaliesPresent',
    accessorKey: 'temperatureAnomaliesPresent',
    header: t('journey.is.temperature.anomalies.present.label', 'Temperature anomalies present'),
    width: 'w-[150px]',
    cell: (row) => (row.temperatureAnomaliesPresent ? <Check className="h-4 w-4" aria-hidden /> : EMPTY_CELL),
    enableSorting: false,
    visible: false
  },
  {
    id: 'uploadedTemperatureLoggersCount',
    accessorKey: 'uploadedTemperatureLoggersCount',
    header: t('journey.temperatureLoggers.uploaded.label', 'Uploaded Temperature Loggers'),
    width: 'w-[150px]',
    className: 'text-right',
    cell: (row) => row.uploadedTemperatureLoggersCount ?? EMPTY_CELL,
    enableSorting: false,
    visible: false
  },
  {
    id: 'attachments',
    header: t('newcmr.attachments.short', 'Attachments'),
    width: 'w-[160px]',
    cell: (row) =>
      row.attachments?.length ? (
        <div className="flex flex-wrap gap-1">
          {row.attachments.map((attachment) => (
            <Badge key={attachment.id} variant="outline" className="max-w-[200px]" title={attachment.name}>
              <span className="truncate">{attachment.name}</span>
            </Badge>
          ))}
        </div>
      ) : (
        EMPTY_CELL
      ),
    enableSorting: false,
    visible: false
  }
];

/** Column presets — Medical View mirrors the JSF preset composition. */
export const getDispatchInProgressColumnPresets = (t: TFunction): ColumnPreset[] => [
  {
    id: 'medical-view',
    label: t('gen.customize.medview.button', 'Medical View'),
    columnIds: [
      'actualDispatchTime',
      'routes',
      'totalValue',
      'expiryDates',
      'temperatureRanges',
      'batchNumbers',
      'senderOrganizations',
      'receiverOrganizations'
    ]
  }
];
